import {gql, useMutation, useQuery, NetworkStatus} from '@apollo/client';
import {
  BuilderAccountCharges,
  BuilderAccountChargesVariables,
} from '../../../schema-types/BuilderAccountCharges';
import useAccountId from '../../../hooks/useAccountId';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {
  BuilderUpdateCharge,
  BuilderUpdateChargeVariables,
} from '../../../schema-types/BuilderUpdateCharge';
import {
  BuilderResetCharge,
  BuilderResetChargeVariables,
} from '../../../schema-types/BuilderResetCharge';
import validateUUID from '../../../utils/validateUUID';

export const CHARGE_TYPE_FRAGMENT = gql`
  fragment BuilderChargeTypeFragment on ChargeType {
    value
    isEditable
    tooltipText
    id
  }
`;

const CHARGE_FRAGMENT = gql`
  ${CHARGE_TYPE_FRAGMENT}
  fragment BuilderBeforeAfterChargeFragment on BeforeAfterChargeType {
    potCharges {
      id
      potName
      potId
      potValue
      productFeeCharge {
        ...BuilderChargeTypeFragment
      }
      dfmFeeCharge {
        ...BuilderChargeTypeFragment
      }
      fpFeeCharge {
        ...BuilderChargeTypeFragment
      }
      sippFeeCharge {
        ...BuilderChargeTypeFragment
      }
      modelPortfolioFeeCharge {
        ...BuilderChargeTypeFragment
      }
      totalChargePercentage
      totalChargePound
    }
    summaryCharge {
      id
      potName
      potValue
      productFeeCharge
      dfmFeeCharge
      fpFeeCharge
      sippFeeCharge
      modelPortfolioFeeCharge
      totalChargePercentage
      totalChargePound
    }
  }
`;

export const GET_ACCOUNT_CHARGES = gql`
  ${CHARGE_FRAGMENT}
  query BuilderAccountCharges($accountId: UUID!, $reviewId: UUID!) {
    chargesDentist(accountId: $accountId, reviewId: $reviewId) {
      errors
      beforeCharge {
        ...BuilderBeforeAfterChargeFragment
      }
      afterCharge {
        ...BuilderBeforeAfterChargeFragment
      }
      initialCharge {
        totalInitialCharge
        charges {
          id
          name
          premiumAmount
          initialFeeChargePercentage
          initialFeeChargePound
        }
        summaryCharge {
          id
          name
          premiumAmount
          initialFeeChargePercentage
          initialFeeChargePound
        }
      }
    }
  }
`;

export const UPDATE_CHARGE = gql`
  mutation BuilderUpdateCharge($id: UUID!, $annualPercentageValue: Decimal!) {
    updateCharge(id: $id, annualPercentageValue: $annualPercentageValue) {
      ok
      errors
    }
  }
`;

export const RESET_CHARGE = gql`
  mutation BuilderResetCharge($reviewId: UUID!) {
    resetCharge(reviewId: $reviewId) {
      ok
      errors
    }
  }
`;

export const usePlanFees = () => {
  const {accountId} = useAccountId();
  const {reviewId} = useReviewContext();

  const {
    data: accountChargesData,
    loading: gLoading,
    error: gError,
    networkStatus,
  } = useQuery<BuilderAccountCharges, BuilderAccountChargesVariables>(
    GET_ACCOUNT_CHARGES,
    {
      variables: {
        accountId: validateUUID(accountId)!,
        reviewId: validateUUID(reviewId)!,
      },
      skip: !validateUUID(accountId) || !reviewId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  );

  const [updateCharge, {data: uData, loading: uLoading, error: uError}] = useMutation<
    BuilderUpdateCharge,
    BuilderUpdateChargeVariables
  >(UPDATE_CHARGE, {
    refetchQueries: [GET_ACCOUNT_CHARGES],
  });

  const [resetCharge, {data: rData, loading: rLoading, error: rError}] = useMutation<
    BuilderResetCharge,
    BuilderResetChargeVariables
  >(RESET_CHARGE, {
    variables: {
      reviewId: validateUUID(reviewId)!,
    },
    refetchQueries: [GET_ACCOUNT_CHARGES],
  });

  return {
    beforeCharge: accountChargesData?.chargesDentist?.beforeCharge,
    updateCharge,
    resetCharge,
    loading: uLoading || rLoading || gLoading || networkStatus === NetworkStatus.refetch,
    error:
      uError?.message ||
      rError?.message ||
      gError?.message ||
      rData?.resetCharge?.errors?.[0] ||
      uData?.updateCharge?.errors?.[0] ||
      accountChargesData?.chargesDentist?.errors?.[0],
    initialCharge: accountChargesData?.chargesDentist?.initialCharge,
    postCharge: accountChargesData?.chargesDentist?.afterCharge,
  };
};
